import React from 'react';
import { Link } from 'react-router-dom';
import { Calendar, Clock, GraduationCap, Image, Users, Video } from 'lucide-react';
import { formatCurrency, formatDate } from '../custom-hooks';
import { useSelector } from 'react-redux';
import { RootState } from '../../redux/rootReducer';
import { isAfricanCountry } from '../../utils/constants';

interface Props {
    imageUrl: string,
    amount: number,
    title: string,
    description: string,
    minAge: number,
    maxAge: number,
    week: number,
    createdAt: any,
    mediaUrl: string,
    altAmount: number,
    attendance: any[],
    classUrl: string,
    docUrl: string,
    childName: string,
    teacher: string,
    isActive: boolean,
    oldClassLink: string,
    level: number,
    cohort: any,
}

function MetaChip({
    icon: Icon,
    label,
    value,
}: {
    icon: React.ElementType;
    label: string;
    value: React.ReactNode;
}) {
    return (
        <div className="inline-flex min-w-0 items-center gap-2 rounded-xl border border-[#efe6f1] bg-white px-3 py-2">
            <Icon size={14} className="shrink-0 text-ryd-primary" />
            <div className="min-w-0">
                <p className="text-[10px] font-semibold uppercase tracking-wide text-[#94a3b8]">{label}</p>
                <p className="truncate text-[13px] font-semibold text-[#334155]">{value}</p>
            </div>
        </div>
    );
}

export default function ActivityCard({
    imageUrl, amount, level,
    altAmount, title,
    description, minAge,
    maxAge, week,
    createdAt, mediaUrl,
    attendance, classUrl,
    docUrl, childName,
    teacher,
    isActive, oldClassLink, cohort
}: Props ) {
    const hasClassAccess = cohort ? (cohort.status === true || (cohort.status === false && cohort.access !== false)) : false;
    const currencyInfo: any = useSelector((state: RootState) => state.user.currency);
    const userInfo: any = useSelector((state: RootState) => state.auth.userInfo);

    const african = isAfricanCountry(userInfo?.country);
    const rate = currencyInfo?.rate || 1;
    const price = formatCurrency((african ? altAmount : amount) * rate);

    const linkBtn =
        'inline-flex items-center gap-2 rounded-[12px] border border-[#E8E0EC] bg-white px-4 py-2.5 text-[13px] font-semibold text-[#476788] transition hover:border-ryd-primary hover:text-ryd-primary';
    const primaryLinkBtn =
        'inline-flex items-center gap-2 rounded-[12px] bg-gradient-to-r from-[#7A2F78] via-ryd-primary to-[#E05AAE] px-4 py-2.5 text-[13px] font-semibold text-white shadow-sm transition hover:scale-[1.01]';

    return (
        <div className="overflow-hidden rounded-[20px] border border-[#efe6f1] bg-white shadow-[0_8px_30px_rgba(136,22,122,0.05)]">
            <div className="grid grid-cols-1 lg:grid-cols-5">
                <div className="p-3 lg:col-span-1">
                    <div className="h-[180px] overflow-hidden rounded-[16px] lg:h-full lg:min-h-[220px]">
                        <img src={imageUrl} alt="" className="h-full w-full object-cover" />
                    </div>
                </div>

                <div className="flex flex-col gap-4 px-5 py-5 sm:px-6 lg:col-span-4">
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                        <div className="min-w-0">
                            <div className="mb-1 flex flex-wrap items-center gap-2">
                                {isActive ? (
                                    <span className="rounded-full bg-emerald-50 px-2.5 py-0.5 text-[11px] font-semibold text-emerald-700">
                                        Active
                                    </span>
                                ) : null}
                                <span className="rounded-full bg-ryd-primaryLess1 px-2.5 py-0.5 text-[11px] font-semibold text-ryd-primary">
                                    {childName}
                                </span>
                            </div>
                            <h1 className="font-[AvertaStd-Semibold] text-[20px] leading-tight text-ryd-headerTextPrimary sm:text-[22px]">
                                {title}
                            </h1>
                            <p className="mt-1.5 text-[14px] leading-relaxed text-[#8b9aab]">{description}</p>
                        </div>
                        <div className="shrink-0 rounded-xl border border-emerald-100 bg-emerald-50 px-3 py-2 text-center">
                            <p className="text-[11px] font-medium text-emerald-700/80">{currencyInfo?.currencyCode}</p>
                            <p className="font-[AvertaStd-Semibold] text-[18px] text-emerald-700">{price}</p>
                        </div>
                    </div>

                    <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 xl:grid-cols-6">
                        <MetaChip icon={Users} label="Age" value={`${minAge}–${maxAge}`} />
                        <MetaChip icon={Clock} label="Duration" value={`${week} weeks`} />
                        <MetaChip icon={Calendar} label="Registered" value={formatDate(createdAt)} />
                        <MetaChip icon={GraduationCap} label="Attendance" value={attendance?.length ?? 0} />
                        <MetaChip icon={Users} label="Teacher" value={teacher.includes('undefined') ? 'No Teacher' : teacher} />
                        <MetaChip icon={Calendar} label="Cohort" value={cohort?.title || 'Unassigned'} />
                    </div>

                    <div className="flex flex-wrap gap-2.5">
                        <Link to={mediaUrl} target="_blank" rel="noopener noreferrer" className={linkBtn}>
                            <Image size={14} /> Media
                        </Link>
                        {oldClassLink ? (
                            <Link to={oldClassLink} className={linkBtn} target="_blank" rel="noopener noreferrer">
                                <Video size={14} /> Previous Class
                            </Link>
                        ) : null}
                        {hasClassAccess ? (
                            <Link to={classUrl} className={primaryLinkBtn} target="_blank" rel="noopener noreferrer">
                                <Video size={14} /> Go to class
                            </Link>
                        ) : null}
                    </div>
                </div>
            </div>
        </div>
    );
}
